import React, { useState, useEffect } from 'react'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Link as MuiLink,
  Container,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import { Bolt } from '@mui/icons-material'
import logoImage from '../assets/logo.webp'
import LanguageToggle from './LanguageToggle'
import { useLanguage } from '../context/LanguageContext'
import { useContactDetails, toTelHref } from '../hooks/useContactDetails'
import PhoneIcon from '@mui/icons-material/Phone'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

const PROMO_KEY = 'dcw_promo_banner_dismissed_ultrafast'

const Navigation: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useLanguage()

  const { phone } = useContactDetails()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [promoDismissed, setPromoDismissed] = useState(
    () => sessionStorage.getItem(PROMO_KEY) === '1'
  )

  const handlePromoDismiss = () => {
    sessionStorage.setItem(PROMO_KEY, '1')
    setPromoDismissed(true)
  }
  
  // State for scroll detection - track if user has scrolled to apply frosted glass effect
  const [isScrolled, setIsScrolled] = useState(false)
  
  // Get current page location so we can highlight the active nav item
  const location = useLocation()
  const theme = useTheme()
  
  // Check if we're on mobile - this helps us decide whether to show hamburger menu
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Listen for scroll events to apply frosted glass effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 50) // Apply effect after scrolling 50px
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Toggle the mobile drawer when user clicks hamburger menu
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  // Handle check availability button click
  const handleCheckAvailabilityClick = () => {
    navigate('/get-started')
  }

  // All our main navigation links - these will be centered in the nav bar
  const navigationItems = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.packages'), href: '/packages' },
    { label: t('nav.switch'), href: '/switch' },
    { label: t('nav.checkServices'), href: '/get-started' },
    { label: t('nav.contact'), href: '/contact' },
    { label: t('nav.support'), href: '/support' },
    { label: t('nav.networkUsage'), href: '/network-usage' },
  ]

  // Check if a nav item should be highlighted as active
  // Home is special - only active when exactly on "/"
  // Other pages are active when the URL starts with their path
  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  // Handle logo click - navigate to home and scroll to top
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (location.pathname !== '/') {
      navigate('/')
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Mobile drawer content - this slides out from the right on mobile devices
  const drawer = (
    <Box sx={{ width: 250 }}>
      {/* Phone number bar at top of mobile drawer */}
      <Box
        component="a"
        href={toTelHref(phone)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: '#00B140',
          color: 'white',
          textDecoration: 'none',
          fontWeight: 800,
          fontSize: '1.05rem',
        }}
      >
        <PhoneIcon sx={{ fontSize: 20 }} />
        {phone}
      </Box>

      {/* Header for mobile drawer with close button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography
          variant="h6"
          component="div"
          onClick={handleLogoClick}
          sx={{
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          {t('nav.companyName')}
        </Typography>
        {/* Close button for mobile drawer */}
        <IconButton 
          onClick={handleDrawerToggle}
          sx={{
            // Ensure minimum 44x44px touch target
            minWidth: 44,
            minHeight: 44,
            width: 44,
            height: 44,
            padding: 1,
            transition: 'all 0.2s ease',
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          aria-label="Close menu"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      {/* Navigation links for mobile */}
      <List>
        {navigationItems.map((item) => {
          const active = isActive(item.href)
          return (
            <ListItem key={item.label} disablePadding>
              <MuiLink
                component={Link}
                to={item.href}
                onClick={handleDrawerToggle}
                sx={{
                  width: '100%',
                  px: 3,
                  // Ensure minimum 44px height for touch target
                  minHeight: 44,
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: active ? 'text.primary' : 'text.secondary',
                  fontWeight: active ? 700 : 400,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    fontWeight: 600,
                  },
                  '&:active': {
                    backgroundColor: 'action.selected',
                    transform: 'scale(0.98)',
                  },
                }}
              >
                <ListItemText primary={item.label} />
              </MuiLink>
            </ListItem>
          )
        })}
        
        {/* About link and Sign Up button for mobile */}
        <ListItem disablePadding>
          <Box sx={{ p: 2, width: '100%' }}>
            <MuiLink
              component={Link}
              to="/about"
              onClick={handleDrawerToggle}
              sx={{
                textDecoration: 'none',
                color: 'primary.main',
                fontWeight: 500,
                display: 'block',
                // Ensure minimum 44px height for touch target
                minHeight: 44,
                py: 1.5,
                mb: 2,
                transition: 'all 0.2s ease',
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              {t('nav.about')}
            </MuiLink>
            <Button
              component="a"
              href="https://portal.directconnectwales.co.uk/login"
              fullWidth
              startIcon={<LockOutlinedIcon />}
              sx={{
                color: '#0A1929',
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 44,
                py: 1.5,
                mb: 1.5,
                border: '1px solid rgba(0,0,0,0.18)',
                borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
              }}
            >
              Login to my account
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleCheckAvailabilityClick}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 44,
                py: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                },
                '&:active': {
                  transform: 'translateY(0) scale(0.98)',
                },
              }}
            >
              Check availability in your area
            </Button>
          </Box>
        </ListItem>
      </List>
    </Box>
  )

  return (
    <>
      {/* Main navigation bar - spans full width with dynamic background */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
          borderTop: isScrolled ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
          boxShadow: isScrolled ? '0 8px 32px 0 rgba(0, 0, 0, 0.15)' : 'none',
          zIndex: 1000,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {/* Promo banner \u2014 sits inside the fixed AppBar so it scrolls with the nav */}
        {!promoDismissed && (
          <Box
            sx={{
              background: 'linear-gradient(90deg, #00521a 0%, #007a28 40%, #DD2033 100%)',
              py: { xs: 0.75, md: 0.9 },
              position: 'relative',
            }}
          >
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, md: 2 }, pr: 4 }}>
                <Bolt sx={{ color: '#fff', fontSize: { xs: 16, md: 18 }, flexShrink: 0 }} />
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    fontWeight: 600,
                    textAlign: 'center',
                    lineHeight: 1.4,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 800 }}>New: </Box>
                  Ultra-fast 1.2Gb &amp; 1.8Gb plans now available \u2014 home &amp; business
                </Typography>
                <Button
                  size="small"
                  onClick={() => { navigate('/packages'); handlePromoDismiss() }}
                  sx={{
                    flexShrink: 0,
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.6)',
                    border: '1px solid',
                    borderRadius: 2,
                    px: { xs: 1.25, md: 2 },
                    py: 0.4,
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    fontWeight: 700,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderColor: '#fff',
                    },
                  }}
                >
                  View plans
                </Button>
              </Box>
            </Container>
            <IconButton
              size="small"
              onClick={handlePromoDismiss}
              aria-label="Dismiss banner"
              sx={{
                position: 'absolute',
                right: { xs: 4, md: 12 },
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                p: 0.5,
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}

        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: { xs: 2, md: 4 },
            py: 3,
            maxWidth: 'none',
            width: '100%',
            minHeight: '80px', // Set minimum height for consistent thickness
            //show on top of other 
          }}
        >
          {/* Company logo on the left side */}
          <Box
            component="img"
            src={logoImage}
            alt="Direct Connect Wales logo"
            onClick={handleLogoClick}
            sx={{
              height: 85,
              maxHeight: 85,
              width: 'auto',
              display: 'block',
              objectFit: 'contain',
              m: 0,
              p: 0,
              my: -6,
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: 0.8
              }
            }}
          />

          {/* Desktop navigation - only show on larger screens */}
          {!isMobile && (
            <>
              {/* Center navigation links - positioned absolutely to center them */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                {navigationItems.map((item) => {
                  const active = isActive(item.href)
                  return (
                    <MuiLink
                      key={item.label}
                      component={Link}
                      to={item.href}
                      sx={{
                        textDecoration: 'none',
                        color: active ? 'text.primary' : 'text.secondary',
                        fontWeight: active ? 700 : 400,
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        '&:hover': {
                          color: 'primary.main',
                          fontWeight: 600,
                        },
                      }}
                    >
                      {item.label}
                    </MuiLink>
                  )
                })}
              </Box>

              {/* Right side actions - Phone, Language toggle, About link and Sign Up button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  component="a"
                  href={toTelHref(phone)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    textDecoration: 'none',
                    color: '#0A1929',
                    fontWeight: 800,
                    fontSize: '1rem',
                    letterSpacing: '0.01em',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    border: '2px solid #00B140',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#00B140',
                      color: 'white',
                      '& .phone-icon': { color: 'white' },
                    },
                  }}
                >
                  <PhoneIcon className="phone-icon" sx={{ fontSize: 18, color: '#00B140', transition: 'color 0.2s ease' }} />
                  {phone}
                </Box>
                <LanguageToggle variant="compact" />
                <MuiLink
                  component={Link}
                  to="/about"
                  sx={{
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                  }}
                >
                  {t('nav.about')}
                </MuiLink>
                <Button
                  component="a"
                  href="https://portal.directconnectwales.co.uk/login"
                  startIcon={<LockOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
                  sx={{
                    color: '#0A1929',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    px: 2,
                    py: 0.9,
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.18)',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.05)',
                      borderColor: 'rgba(0,0,0,0.35)',
                    },
                  }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCheckAvailabilityClick}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  Check availability in your area
                </Button>
              </Box>
            </>
          )}

          {/* Mobile hamburger menu button - only show on small screens */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                color: 'text.primary',
                // Ensure minimum 44x44px touch target
                minWidth: 44,
                minHeight: 44,
                width: 44,
                height: 44,
                padding: 1,
                transition: 'all 0.2s ease',
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer - slides out from right side on mobile devices */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: 280, sm: 320 },
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          },
          '& .MuiBackdrop-root': {
            transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  )
}

export default Navigation
